import 'Components/atoms/vm-bottom-sheet/vm-bottom-sheet';
import type { VmBottomSheet } from 'Components/atoms/vm-bottom-sheet/vm-bottom-sheet';
import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-loading-spinner/vm-loading-spinner';
import { MediaQueryController } from 'Components/controllers/media-query-controller';
import 'Video/components/vm-video-comment-composer/vm-video-comment-composer';
import './vm-video-comments-list';
import './vm-video-comments-list-item';
import { LitElement, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { createInfiniteQueryController, createMutationController } from '@tanstack/lit-query';
import type { InfiniteData } from '@tanstack/query-core';
import { VmVideoCommentComposer } from 'Video/components/vm-video-comment-composer/vm-video-comment-composer';
import { commentsQueryOptions, postComment, type CommentData, type CommentsApiResponse } from 'Api/comments';
import { queryClient } from 'Api/query-client';
import { VideoCommentEvents, trackVideoCommentEvent } from 'Analytics/video-comment-events';
import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';
import Styles from './vm-video-comments.scss';

/**
 * Appends `reply` to the root-level `children[]` of whichever root matches
 * `parentId` or already contains it.
 *
 * Always inserts at root depth because `CommentThreadSerializer` flattens every
 * descendant into the root's `children`, so the client only sees depth ≤ 1. A
 * naïve recursive walk would create a depth-2 node that visibly jumps up a level
 * on the next refetch.
 */
function insertReplyIntoTree(nodes: CommentData[], parentId: number, reply: CommentData): CommentData[] {
    return nodes.map((node) => {
        const isRoot = node.id === parentId;
        const containsReply = node.children?.some((child) => child.id === parentId) ?? false;
        if (isRoot || containsReply) {
            return { ...node, children: [...(node.children ?? []), reply] };
        }
        return node;
    });
}

export function applyPostedToCache(
    old: InfiniteData<CommentsApiResponse, number> | undefined,
    posted: CommentData,
    replyToId: number | null,
): InfiniteData<CommentsApiResponse, number> {
    if (!old) {
        return {
            pages: [
                {
                    count: replyToId ? 0 : 1,
                    total_count: 1,
                    results: replyToId ? [] : [posted],
                },
            ],
            pageParams: [0],
        };
    }
    if (replyToId) {
        // `count` is top-level only and drives `hasNextPage`, so a reply bumps
        // `total_count` alone.
        const updatedPages = old.pages.map((page) => ({
            ...page,
            results: insertReplyIntoTree(page.results, replyToId, posted),
        }));
        if (updatedPages[0]) {
            updatedPages[0] = {
                ...updatedPages[0],
                total_count: updatedPages[0].total_count + 1,
            };
            return { ...old, pages: updatedPages };
        }
        // Cache with zero pages: seed one so the count still ticks up and
        // TanStack's `pages.length === pageParams.length` invariant holds.
        return {
            ...old,
            pages: [{ count: 0, total_count: 1, results: [] }],
            pageParams: [0],
        };
    }
    const [firstPage, ...restPages] = old.pages;
    const updatedFirst: CommentsApiResponse = {
        count: (firstPage?.count ?? 0) + 1,
        total_count: (firstPage?.total_count ?? 0) + 1,
        results: [posted, ...(firstPage?.results ?? [])],
    };
    // Empty `pageParams` would leave `getNextPageParam` with
    // `lastPageParam=undefined`.
    const pageParams = old.pageParams.length > 0 ? old.pageParams : [0];
    return { ...old, pages: [updatedFirst, ...restPages], pageParams };
}

/**
 * Video-page comments organism. Desktop renders inline; mobile wraps the list in
 * `<vm-bottom-sheet>`, opened by `vm-video-comments-button:open` on `document`.
 *
 * Reply mode is split by viewport: desktop opens an inline composer per item
 * (`_openReplies`), mobile reuses the single top composer with a "Replying to
 * <Name>" chip (`_replyToId` / `_replyToName`).
 *
 * The submit / reply / composer-focus listeners live on the inner `.comments`
 * wrapper, not the host: events must originate INSIDE the shadow subtree to be
 * caught, so dispatching `vm-comment-submit` on the host from outside bubbles
 * up the light DOM and never reaches the wrapper.
 *
 * @fires vm-video-comments:sheet-visibility - bubbles + composed. Detail:
 *   `{ open: boolean }`. Fires on the mobile-sheet open/close transition; CLOSE
 *   also fires on a mobile→desktop resize with the sheet still open. Distinct
 *   from the `SHEET_OPENED` / `SHEET_CLOSED` Mixpanel events.
 */
@customElement('vm-video-comments')
export class VmVideoComments extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'login-url' }) loginUrl = '';
    @property({ type: String, attribute: 'video-slug' }) videoSlug = '';
    @property({ type: Boolean, attribute: 'disable-comments' }) disableComments = false;
    @property({ type: Boolean, attribute: 'user-authenticated' }) userAuthenticated = false;
    @property({ type: String, attribute: 'user-avatar-src' }) userAvatarSrc = '';
    @property({ type: String, attribute: 'user-name' }) userName = '';
    @property({ type: String, attribute: 'user-profile-url' }) userProfileUrl = '';
    @property({ type: Boolean, attribute: 'can-view-comments' }) canViewComments = false;
    @property({ type: Boolean, attribute: 'can-add-comments' }) canAddComments = false;

    @state() dataFetched = false;
    @state() private _replyToId: number | null = null;
    @state() private _replyToName = '';
    @state() private _sheetOpen = false;
    /**
     * Set on composer focus and held for the rest of the sheet's open lifetime.
     * Without it, iOS scrolls the layout viewport to reveal the focused input and
     * takes the sheet header off-screen with it.
     */
    @state() private _sheetFullscreen = false;
    /** Replaced, never mutated — Lit's `!==` @state check won't fire otherwise. */
    @state() private _openReplies = new Set<number>();

    @query('vm-video-comment-composer') private _composer?: VmVideoCommentComposer;
    @query('.comments__load-more') private _loadMoreEl?: HTMLElement;
    @query('vm-bottom-sheet') private _sheet?: VmBottomSheet;

    // Tracked so a desktop↔mobile swap, or the sheet body arriving a microtask
    // later, rebuilds the observer rather than stranding it on the old element
    // or the wrong root.
    private _observedLoadMoreEl: HTMLElement | null = null;
    private _observedRoot: Element | null = null;
    private _loadMoreObserver: IntersectionObserver | null = null;

    private _commentsQuery = createInfiniteQueryController(
        this,
        () => ({
            ...commentsQueryOptions(this.videoSlug),
            enabled: !!this.videoSlug && this.canViewComments,
        }),
        queryClient,
    );

    /**
     * Post-hoc write rather than optimistic — a create with a server-assigned id
     * shouldn't guess. Loading is scoped per-submitter via `targetComposer`, not
     * `mutation.isPending`, so concurrent desktop reply composers don't both
     * light up on either submit.
     */
    private _postComment = createMutationController<
        CommentData,
        Error,
        {
            text: string;
            attachmentIds: number[];
            replyToId: number | null;
            targetComposer: VmVideoCommentComposer | null;
        },
        void
    >(
        this,
        () => ({
            mutationFn: ({ text, attachmentIds, replyToId }) =>
                postComment({
                    videoSlug: this.videoSlug,
                    comment: text,
                    attachmentIds,
                    parentId: replyToId ?? undefined,
                }),

            onSuccess: (response, { replyToId, targetComposer }) => {
                // Synchronous: `onSettled` unlocks Send as soon as this returns, so
                // deferring the clear lets a double-tap repost the draft.
                targetComposer?.clear();
                // Guarded because the chip may have retargeted to another parent
                // while this reply was in flight; wiping it would turn the next
                // Send into a top-level post.
                if (replyToId && this._replyToId === replyToId) {
                    this._replyToId = null;
                    this._replyToName = '';
                }

                // Detached on purpose: query-core runs `onSuccess` inside
                // `Mutation.execute`'s try block, so a throw in here would route to
                // `onError` and show "Could not post" for a comment that persisted.
                // As an unhandled rejection it reaches Sentry instead.
                (async () => {
                    const key = commentsQueryOptions(this.videoSlug).queryKey;
                    await queryClient.cancelQueries({ queryKey: key });
                    queryClient.setQueryData<InfiniteData<CommentsApiResponse, number>>(key, (old) =>
                        applyPostedToCache(old, response, replyToId),
                    );

                    if (replyToId) this._closeReply(replyToId);

                    const coreProps = {
                        comment_id: response.id || null,
                        is_public: response.is_public ?? null,
                        attachment_count: response.attachments?.length || 0,
                    };
                    if (replyToId) {
                        trackVideoCommentEvent(VideoCommentEvents.REPLY_POSTED, {
                            ...coreProps,
                            reply_to_comment_id: replyToId,
                        });
                    } else {
                        trackVideoCommentEvent(VideoCommentEvents.POSTED, coreProps);
                    }
                })();
            },

            onError: () => {
                // Reply state is left alone so a retry still targets the same parent.
                document.dispatchEvent(
                    new CustomEvent('vm-toast-show', {
                        detail: { message: 'Could not post comment. Please try again.', variant: 'error' },
                    }),
                );
            },

            onSettled: (_data, _err, variables) => {
                if (variables.targetComposer) variables.targetComposer.loading = false;
            },
        }),
        queryClient,
    );

    /**
     * Top-level comments across all fetched pages.
     *
     * Paging is offset-based, so a comment posted between two page fetches
     * pushes every row after it down by one. A comment sitting on a page
     * boundary can then arrive in both pages, hence the dedup by id.
     */
    private get _items(): CommentData[] {
        const pages = this._commentsQuery.current.data?.pages ?? [];
        const seen = new Set<number>();
        const out: CommentData[] = [];
        for (const page of pages) {
            // `results` is normally always an array on well-formed pages, but a
            // malformed response (e.g. a fetch stub or partial-JSON error path)
            // can leave it undefined. Fall back to an empty array so a single
            // bad page can't blow up the whole render.
            for (const c of page.results ?? []) {
                if (!seen.has(c.id)) {
                    seen.add(c.id);
                    out.push(c);
                }
            }
        }
        return out;
    }

    private get _totalComments(): number {
        return this._commentsQuery.current.data?.pages[0]?.total_count ?? 0;
    }

    private get _hasMore(): boolean {
        return this._commentsQuery.current.hasNextPage;
    }

    private get _loadingMore(): boolean {
        return this._commentsQuery.current.isFetchingNextPage;
    }

    private _desktop = new MediaQueryController(this, DESKTOP_BREAKPOINT_QUERY, (isDesktop) => {
        // Each viewport's reply UI is cleared on the way out: the chip would leak
        // into the inline flow, and inline composers have no mobile design.
        if (isDesktop) {
            // Otherwise a resize strands the sheet open with nothing to close it.
            this._setSheetOpen(false);
            this._onReplyCancel();
        } else if (this._openReplies.size > 0) {
            this._openReplies = new Set();
        }
    });

    /**
     * The single transition point for sheet open state, so the analytics and the
     * visibility event can't drift from it.
     */
    private _setSheetOpen(open: boolean): void {
        if (this._sheetOpen === open) return;
        this._sheetOpen = open;
        // The next open starts at the sheet's default height.
        if (!open) this._sheetFullscreen = false;

        trackVideoCommentEvent(open ? VideoCommentEvents.SHEET_OPENED : VideoCommentEvents.SHEET_CLOSED, {});

        this.dispatchEvent(
            new CustomEvent('vm-video-comments:sheet-visibility', {
                detail: { open },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private _onSheetOpenRequest = (e: Event): void => {
        if (e.defaultPrevented || this._desktop.matches) return;
        this._setSheetOpen(true);
    };

    /** Mobile-only — on desktop the item opens its own inline composer. */
    private _onReplySelect = (e: CustomEvent<{ commentId: number; authorName: string }>) => {
        if (this._desktop.matches) return;
        this._replyToId = e.detail.commentId;
        this._replyToName = e.detail.authorName;
        this._setSheetOpen(true);
        this.updateComplete.then(() => {
            this._composer?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            this._composer?.focus();
        });
    };

    private _onReplyCancel = () => {
        this._replyToId = null;
        this._replyToName = '';
    };

    private _onReplyItemOpen = (e: CustomEvent<{ parentId: number }>) => {
        this._openReply(e.detail.parentId);
    };

    private _onReplyItemClose = (e: CustomEvent<{ parentId: number }>) => {
        this._closeReply(e.detail.parentId);
    };

    private _openReply(id: number) {
        if (this._openReplies.has(id)) return;
        this._openReplies = new Set(this._openReplies).add(id);
    }

    private _closeReply(id: number) {
        if (!this._openReplies.has(id)) return;
        const next = new Set(this._openReplies);
        next.delete(id);
        this._openReplies = next;
    }

    private _onComposerFocus = () => {
        if (this._desktop.matches || !this._sheetOpen) return;
        this._sheetFullscreen = true;
    };

    private _onCommentSubmit = (
        e: CustomEvent<{ text: string; attachmentIds: number[]; replyToId: number | null }>,
    ) => {
        const { text, attachmentIds, replyToId } = e.detail;
        if (!text.trim() && attachmentIds.length === 0) return;

        // Take over the submit so the composer keeps the draft until the POST resolves.
        e.preventDefault();

        // `composedPath()[0]` rather than `target`, which retargets across shadow
        // boundaries. Knowing the exact composer keeps its loading state scoped, so
        // a concurrent reply composer stays interactive.
        const composer = e.composedPath()[0];
        const targetComposer = composer instanceof VmVideoCommentComposer ? composer : null;

        // Synchronous, not in `onMutate` — tanstack awaits that, and the microtask
        // gap is enough for a fast double-send to slip a second submit through.
        if (targetComposer) targetComposer.loading = true;

        this._postComment.mutate({ text, attachmentIds, replyToId, targetComposer });
    };

    override async connectedCallback() {
        super.connectedCallback();
        document.addEventListener('vm-video-comments-button:open', this._onSheetOpenRequest);

        if (this.videoSlug) {
            // Hydrated even without view permission: the seed still carries the
            // counts, and the anon card reads its label from the cache.
            const hydrated = this._hydrateFromInitial();
            if (!hydrated && this.canViewComments) {
                try {
                    await queryClient.fetchInfiniteQuery(commentsQueryOptions(this.videoSlug));
                } catch {
                    // Surfaced via the controller.
                }
            }
        }
        this.dataFetched = true;
    }

    /**
     * Seeds the query cache from the `data-initial-comments` attribute the
     * video-detail template emits, so there's no round-trip on first paint.
     * Returns `false` on a missing or malformed payload, degrading to the normal
     * fetch rather than corrupting the cache.
     */
    private _hydrateFromInitial(): boolean {
        const raw = this.dataset.initialComments;
        if (!raw) return false;
        let seed: CommentsApiResponse;
        try {
            seed = JSON.parse(raw) as CommentsApiResponse;
        } catch {
            return false;
        }
        if (
            !seed ||
            typeof seed.count !== 'number' ||
            typeof seed.total_count !== 'number' ||
            !Array.isArray(seed.results)
        ) {
            return false;
        }
        queryClient.setQueryData<InfiniteData<CommentsApiResponse, number>>(
            commentsQueryOptions(this.videoSlug).queryKey,
            { pages: [seed], pageParams: [0] },
        );
        return true;
    }

    override updated() {
        this._setupLoadMoreObserver();
    }

    override disconnectedCallback() {
        document.removeEventListener('vm-video-comments-button:open', this._onSheetOpenRequest);
        this._loadMoreObserver?.disconnect();
        this._loadMoreObserver = null;
        super.disconnectedCallback();
    }

    private _setupLoadMoreObserver(): void {
        // On mobile the list scrolls inside the sheet body, so that's the root the
        // prefetch margin has to be measured against; desktop uses the viewport.
        const root = this._desktop.matches ? null : (this._sheet?.scrollContainer ?? null);

        // An observer's root is fixed at construction, so a change to either means
        // rebuilding rather than re-observing.
        if (this._loadMoreEl === this._observedLoadMoreEl && root === this._observedRoot) return;

        this._loadMoreObserver?.disconnect();
        this._loadMoreObserver = null;
        this._observedLoadMoreEl = this._loadMoreEl ?? null;
        this._observedRoot = root;

        if (!this._loadMoreEl) return;

        this._loadMoreObserver = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && this._hasMore && !this._loadingMore) {
                    this._commentsQuery.fetchNextPage();
                }
            },
            { root, rootMargin: '200px' },
        );
        this._loadMoreObserver.observe(this._loadMoreEl);

        // `<vm-bottom-sheet>` renders after this component — rerun when it's ready.
        // `=== false` because an unupgraded element has `undefined`, not `false`.
        if (!this._desktop.matches && this._sheet?.hasUpdated === false) {
            this._sheet.updateComplete.then(() => this.requestUpdate());
        }
    }

    override render() {
        if (!this.dataFetched) {
            return html`
                <div class="comments">
                    <vm-loading-spinner size="xl"></vm-loading-spinner>
                </div>
            `;
        }

        if (this.disableComments) {
            return html`
                <div class="comments">
                    <span class="comments__title">Comments are disabled for this content.</span>
                </div>
            `;
        }

        if (!this.userAuthenticated) {
            return this._renderAnonCard();
        }

        // Nothing at all rather than a login card — comments are hidden by policy
        // here, not by a login gate.
        if (!this.canViewComments) {
            return nothing;
        }

        if (!this._desktop.matches) {
            return html`
                <vm-bottom-sheet
                    ?open=${this._sheetOpen}
                    ?fullscreen=${this._sheetFullscreen}
                    @vm-close=${() => {
                        this._setSheetOpen(false);
                        // Otherwise the next open inherits "Replying to Jane" and
                        // silently posts as a reply to her.
                        this._onReplyCancel();
                    }}
                >
                    <span slot="header">Comments</span>
                    ${this._renderCommentsWrapper()}
                </vm-bottom-sheet>
            `;
        }

        return this._renderCommentsWrapper();
    }

    /**
     * Mobile puts the composer after the list so `position: sticky` can pin it to
     * the bottom of the sheet, and drops the title because the sheet header
     * already carries it.
     */
    private _renderCommentsWrapper() {
        const inner = this._desktop.matches
            ? html`
                  <p class="comments__title">${this._totalComments} Comments</p>
                  ${this._renderComposer()} ${this._renderList()}
              `
            : html`${this._renderList()} ${this._renderComposer()}`;

        return html`
            <div
                class="comments"
                @vm-video-comment-reply:select=${this._onReplySelect}
                @vm-comment-submit=${this._onCommentSubmit}
                @vm-comment-reply-cancel=${this._onReplyCancel}
                @vm-video-comment-composer:focus=${this._onComposerFocus}
                @vm-video-comments-list-item:reply-open=${this._onReplyItemOpen}
                @vm-video-comments-list-item:reply-close=${this._onReplyItemClose}
            >
                ${inner}
            </div>
        `;
    }

    private _renderAnonCard() {
        const label = this._totalComments === 1 ? '1 comment' : `${this._totalComments} comments`;
        return html`
            <div class="comments">
                <div class="comments__login-card">
                    <p class="comments__login-card-text">
                        <strong>${label}</strong>
                        <span class="comments__login-card-sep">•</span>
                        <span>Login or sign up to view comments.</span>
                    </p>
                    ${this.loginUrl
                        ? html`
                              <vm-button variant="solid" color-scheme="primary" size="sm" href=${this.loginUrl}>
                                  Login or sign up
                              </vm-button>
                          `
                        : nothing}
                </div>
            </div>
        `;
    }

    private _renderComposer() {
        if (!this.canAddComments) return nothing;
        // Desktop replies go to an inline composer, so this one never carries reply
        // state; on mobile it's the only composer there is.
        return html`
            <vm-video-comment-composer
                video-slug=${this.videoSlug}
                avatar-src=${this.userAvatarSrc}
                avatar-name=${this.userName}
                avatar-link=${this.userProfileUrl}
                .replyToId=${this._desktop.matches ? null : this._replyToId}
                .replyToName=${this._desktop.matches ? '' : this._replyToName}
            ></vm-video-comment-composer>
        `;
    }

    private _renderList() {
        // Items are composed here rather than inside `<vm-video-comments-list>` so
        // props reach each one directly, without a forwarding layer.
        return html`
            <div class="comments__wrapper">
                ${this._items.length
                    ? html`
                          <vm-video-comments-list>
                              ${repeat(
                                  this._items,
                                  (c) => c.id,
                                  (c) => html`
                                      <vm-video-comments-list-item
                                          .comment=${c}
                                          .dataAddCommentsPerm=${this.canAddComments}
                                          .videoSlug=${this.videoSlug}
                                          .userAvatarSrc=${this.userAvatarSrc}
                                          .userAvatarName=${this.userName}
                                          .userProfileUrl=${this.userProfileUrl}
                                          .openReplies=${this._openReplies}
                                          .isDesktop=${this._desktop.matches}
                                      ></vm-video-comments-list-item>
                                  `,
                              )}
                          </vm-video-comments-list>
                      `
                    : nothing}
                ${this._hasMore
                    ? html`
                          <div class="comments__load-more">
                              ${this._loadingMore ? html`<vm-loading-spinner size="lg"></vm-loading-spinner>` : nothing}
                          </div>
                      `
                    : nothing}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-comments': VmVideoComments;
    }
}
